/**
 * ModuleEditorForm - Full-screen editor form for module definitions.
 * Used in the EditorDrawer for creating/editing modules.
 */
import {
	BarChart,
	BarChart3,
	Bell,
	Box,
	Building,
	Calendar,
	ChartPie,
	ChevronDown,
	CircleDollarSign,
	Cog,
	Database,
	FileText,
	Folder,
	Globe,
	Home,
	Inbox,
	LayoutDashboard,
	LineChart,
	Mail,
	Map,
	Megaphone,
	MessageSquare,
	Monitor,
	PieChart,
	Search,
	Settings,
	ShoppingCart,
	Target,
	TrendingUp,
	User,
	Users,
	Zap,
} from "lucide-react";
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import { ActionEditor } from "@/components/actions";
import { EditorShell } from "@/components/layout/EditorShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { getRegisteredModuleKeys, formatComponentKeyAsName } from "@/modules/registry";

const iconMap: Record<string, typeof LayoutDashboard> = {
	BarChart,
	BarChart3,
	Bell,
	Box,
	Building,
	Calendar,
	ChartPie,
	CircleDollarSign,
	Cog,
	Database,
	FileText,
	Folder,
	Globe,
	Home,
	Inbox,
	LayoutDashboard,
	LineChart,
	Mail,
	Map,
	Megaphone,
	MessageSquare,
	Monitor,
	PieChart,
	Search,
	Settings,
	ShoppingCart,
	Target,
	TrendingUp,
	User,
	Users,
	Zap,
};

const ICON_OPTIONS = Object.keys(iconMap);

interface ModuleEditorFormProps {
	/** Module ID to edit, or null for create mode */
	moduleId: string | null;
}

/**
 * Full-screen editor form for modules.
 * Manages its own form state internally for performance.
 * Closes by clearing URL search params.
 * @param props - Component props
 * @param props.moduleId - Module ID to edit, or null for create mode
 */
export function ModuleEditorForm({ moduleId }: ModuleEditorFormProps) {
	const [, setSearchParams] = useSearchParams();
	const utils = trpc.useUtils();

	const isCreateMode = moduleId === null;

	// Form state
	const [name, setName] = useState("");
	const [slug, setSlug] = useState("");
	const [icon, setIcon] = useState("");
	const [iconPickerOpen, setIconPickerOpen] = useState(false);
	const [description, setDescription] = useState("");
	const [heroImage, setHeroImage] = useState("");
	const [groupId, setGroupId] = useState<string | null>(null);
	const [enabled, setEnabled] = useState(true);
	const [sequentialExecution, setSequentialExecution] = useState(true);
	const [creationMode, setCreationMode] = useState<"workflow" | "custom">("workflow");
	const [moduleType, setModuleType] = useState<"workflow" | "custom_interface">("workflow");
	const [componentKey, setComponentKey] = useState<string | null>(null);
	const [autoPopulatedName, setAutoPopulatedName] = useState<string | null>(null);

	// Track if form has been modified
	const [isDirty, setIsDirty] = useState(false);

	// Query for module data (only in edit mode)
	const moduleQuery = trpc.modules.core.getById.useQuery(
		{ moduleId: moduleId ?? "" },
		{ enabled: !isCreateMode && !!moduleId }
	);

	// Query for groups (always)
	const groupsQuery = trpc.modules.core.listAll.useQuery();

	// Create mutation
	const createMutation = trpc.modules.core.create.useMutation({
		onSuccess: () => {
			void utils.modules.core.listAll.invalidate();
			void utils.modules.list.invalidate();
			closeDrawer();
		},
	});

	// Update mutation
	const updateMutation = trpc.modules.core.update.useMutation({
		onSuccess: () => {
			void utils.modules.core.listAll.invalidate();
			void utils.modules.list.invalidate();
			void utils.modules.core.getById.invalidate({ moduleId: moduleId ?? "" });
		},
	});

	/**
	 * Closes the drawer by clearing URL params
	 */
	const closeDrawer = () => {
		setSearchParams({});
	};

	// Initialize form when data loads (edit mode)
	useEffect(() => {
		if (!isCreateMode && moduleQuery.data?.module) {
			const m = moduleQuery.data.module;
			setName(m.name);
			setSlug(m.slug);
			setIcon(m.icon ?? "");
			setDescription(m.description ?? "");
			setHeroImage(m.heroImage ?? "");
			setGroupId(m.groupId);
			setEnabled(m.enabled);
			setSequentialExecution(m.sequentialExecution);
			setIsDirty(false);
		}
	}, [moduleQuery.data, isCreateMode]);

	// Reset form when switching to create mode
	useEffect(() => {
		if (isCreateMode) {
			setName("");
			setSlug("");
			setIcon("");
			setDescription("");
			setHeroImage("");
			setGroupId(null);
			setEnabled(true);
			setSequentialExecution(true);
			setCreationMode("workflow");
			setModuleType("workflow");
			setComponentKey(null);
			setAutoPopulatedName(null);
			setIsDirty(false);
		}
	}, [isCreateMode]);

	// Auto-generate slug from name (create mode only)
	useEffect(() => {
		if (isCreateMode && name && !isDirty) {
			const generatedSlug = name
				.toLowerCase()
				.replace(/[^a-z0-9]+/g, "-")
				.replace(/^-+|-+$/g, "");
			setSlug(generatedSlug);
		}
	}, [name, isCreateMode, isDirty]);

	/**
	 * Creates a change handler that marks the form as dirty
	 * @param setter - the state setter function
	 */
	const handleFieldChange = <T,>(setter: (value: T) => void) => (value: T) => {
		setter(value);
		setIsDirty(true);
	};

	/**
	 * Handles selection of a custom module from the registry list.
	 * Auto-populates name and slug, but only if they haven't been manually edited.
	 * @param key - the registry component key that was selected
	 */
	const handleCustomModuleSelect = (key: string) => {
		setComponentKey(key);
		const displayName = formatComponentKeyAsName(key);
		// Only auto-populate name if it is empty or matches the previous auto-populated value
		if (!name || name === autoPopulatedName) {
			setName(displayName);
			setAutoPopulatedName(displayName);
		}
		// Only auto-populate slug if it hasn't been manually edited
		const prevAutoSlug = autoPopulatedName
			?.toLowerCase()
			.replace(/[^a-z0-9]+/g, "-")
			.replace(/^-+|-+$/g, "");
		if (!slug || slug === prevAutoSlug) {
			setSlug(key);
		}
		setIsDirty(true);
	};

	/**
	 * Saves the module (creates or updates based on mode)
	 */
	const handleSave = () => {
		if (isCreateMode) {
			createMutation.mutate({
				name,
				slug,
				icon: icon || null,
				description: creationMode === "workflow" ? (description || null) : null,
				heroImage: creationMode === "workflow" ? (heroImage || null) : null,
				groupId,
				order: 0,
				enabled,
				sequentialExecution: creationMode === "workflow" ? sequentialExecution : false,
				moduleType,
				componentKey: creationMode === "custom" ? componentKey : null,
			});
		}
		else if (moduleId) {
			updateMutation.mutate({
				moduleId,
				name,
				slug,
				icon: icon || null,
				description: description || null,
				heroImage: heroImage || null,
				groupId,
				enabled,
				sequentialExecution,
			});
		}
	};

	// Get the icon component for preview
	const PreviewIcon = iconMap[icon] ?? LayoutDashboard;

	const groups = groupsQuery.data?.groups ?? [];
	const isPending = createMutation.isPending || updateMutation.isPending;
	const mutationError = createMutation.error ?? updateMutation.error;

	// Loading state (edit mode only)
	if (!isCreateMode && moduleQuery.isLoading) {
		return (
			<EditorShell
				title="Edit Module"
				onBack={closeDrawer}
			>
				<div className="flex items-center justify-center py-12">
					<div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
				</div>
			</EditorShell>
		);
	}

	// Error state (edit mode only)
	if (!isCreateMode && moduleQuery.error) {
		return (
			<EditorShell
				title="Edit Module"
				onBack={closeDrawer}
			>
				<div className="text-center py-12">
					<p className="text-red-400">Error loading module</p>
					<p className="text-sm text-muted-foreground mt-2">
						{moduleQuery.error.message}
					</p>
					<Button className="mt-4" onClick={closeDrawer}>
						Go Back
					</Button>
				</div>
			</EditorShell>
		);
	}

	return (
		<EditorShell
			title={isCreateMode ? "Create Module" : "Edit Module"}
			description={isCreateMode
				? "Configure a new module for the platform."
				: `Editing: ${name || "Untitled Module"}`}
			onBack={closeDrawer}
			constrainWidth={false}
			footerActions={
				<>
					<Button variant="outline" type="button" onClick={closeDrawer}>
						Cancel
					</Button>
					<Button
						onClick={handleSave}
						disabled={isCreateMode
							? (!name || !slug || (creationMode === "custom" && !componentKey) || isPending)
							: (!isDirty || !name || !slug || isPending)}
					>
						{isPending
							? (isCreateMode ? "Creating..." : "Saving...")
							: (isCreateMode ? "Create Module" : "Save Changes")}
					</Button>
				</>
			}
		>
			{/* Error message */}
			{mutationError && (
				<div className="mb-6 rounded-lg border border-destructive bg-destructive/10 p-4 text-red-400">
					{mutationError.message}
				</div>
			)}

			{/* Creation Mode Toggle (create mode only) */}
			{isCreateMode && (
				<div className="mb-6">
					<Label className="text-base font-semibold">Module Type</Label>
					<p className="text-sm text-muted-foreground mb-3">
						Choose what kind of module to create.
					</p>
					<RadioGroup
						value={creationMode}
						onValueChange={(value: string) => {
							const mode = value as "workflow" | "custom";
							setCreationMode(mode);
							if (mode === "workflow") {
								setModuleType("workflow");
								setComponentKey(null);
								setName("");
								setSlug("");
							}
							else {
								setModuleType("custom_interface");
							}
							setIsDirty(false);
						}}
						className="flex gap-4"
					>
						<div className="flex items-center space-x-2">
							<RadioGroupItem value="workflow" id="mode-workflow" />
							<Label htmlFor="mode-workflow">Workflow Module</Label>
						</div>
						<div className="flex items-center space-x-2">
							<RadioGroupItem value="custom" id="mode-custom" />
							<Label htmlFor="mode-custom">Custom Module</Label>
						</div>
					</RadioGroup>
				</div>
			)}

			{/* Configuration Grid */}
			<div className="grid gap-6 lg:grid-cols-2">
				{/* Basic Settings Card */}
				<Card>
					<CardHeader>
						<CardTitle>
							{isCreateMode && creationMode === "custom" ? "Select Custom Module" : "Basic Settings"}
						</CardTitle>
						<CardDescription>
							{isCreateMode && creationMode === "custom"
								? "Choose a registered custom module to create."
								: "Configure the module\u2019s display and behavior settings."}
						</CardDescription>
					</CardHeader>
					<CardContent className="space-y-4">
						{isCreateMode && creationMode === "custom" ? (
							<>
								{/* Custom module selection list */}
								{getRegisteredModuleKeys().length === 0 ? (
									<p className="text-sm text-muted-foreground py-4 text-center">
										No custom modules are registered. Add modules to the registry at
										apps/web/src/modules/registry.ts.
									</p>
								) : (
									<RadioGroup
										value={componentKey ?? ""}
										onValueChange={handleCustomModuleSelect}
										className="space-y-2"
									>
										{getRegisteredModuleKeys().map((key) => (
											<label
												key={key}
												className={cn(
													"flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors hover:bg-accent",
													componentKey === key && "border-primary bg-accent"
												)}
											>
												<RadioGroupItem value={key} />
												<div>
													<div className="font-medium">{formatComponentKeyAsName(key)}</div>
													<div className="text-xs text-muted-foreground">{key}</div>
												</div>
											</label>
										))}
									</RadioGroup>
								)}

								{/* Editable name/slug fields, shown only after a module is selected */}
								{componentKey && (
									<>
										<div className="grid gap-2 pt-2">
											<Label htmlFor="module-name">Name</Label>
											<Input
												id="module-name"
												placeholder="Module name"
												value={name}
												onChange={(e) => { handleFieldChange(setName)(e.target.value); }}
											/>
										</div>
										<div className="grid gap-2">
											<Label htmlFor="module-slug">Slug</Label>
											<Input
												id="module-slug"
												placeholder="module-slug"
												value={slug}
												onChange={(e) => { handleFieldChange(setSlug)(e.target.value); }}
											/>
											<p className="text-xs text-muted-foreground">
												URL path for this module (lowercase letters, numbers, and hyphens only)
											</p>
										</div>
									</>
								)}

								{/* Icon picker */}
								<div className="grid gap-2">
									<Label>Icon</Label>
									<Popover open={iconPickerOpen} onOpenChange={setIconPickerOpen}>
										<PopoverTrigger asChild>
											<Button variant="outline" className="w-full justify-between">
												<span className="flex items-center gap-2">
													<PreviewIcon className="h-4 w-4" />
													{icon || "LayoutDashboard"}
												</span>
												<ChevronDown className="h-4 w-4 opacity-50" />
											</Button>
										</PopoverTrigger>
										<PopoverContent className="w-80 p-3" align="start">
											<div className="grid grid-cols-6 gap-2">
												{ICON_OPTIONS.map((iconName) => {
													const IconComponent = iconMap[iconName] ?? LayoutDashboard;
													const isSelected = (icon || "LayoutDashboard") === iconName;
													return (
														<button
															key={iconName}
															type="button"
															onClick={() => {
																handleFieldChange(setIcon)(iconName);
																setIconPickerOpen(false);
															}}
															className={cn(
																"flex h-9 w-9 items-center justify-center rounded-md border transition-colors hover:bg-accent",
																isSelected && "border-primary bg-accent"
															)}
															title={iconName}
														>
															<IconComponent className="h-4 w-4" />
														</button>
													);
												})}
											</div>
										</PopoverContent>
									</Popover>
								</div>

								{/* Group selector */}
								<div className="grid gap-2">
									<Label htmlFor="module-group">Group</Label>
									<Select
										value={groupId ?? "ungrouped"}
										onValueChange={(v) => { handleFieldChange(setGroupId)(v === "ungrouped" ? null : v); }}
									>
										<SelectTrigger>
											<SelectValue placeholder="Select a group" />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="ungrouped">Ungrouped</SelectItem>
											{groups.map((group) => (
												<SelectItem key={group.id} value={group.id}>
													{group.name ?? "(No name)"}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>

								{/* Enabled toggle */}
								<div className="flex items-center gap-2 pt-2">
									<Checkbox
										id="module-enabled"
										checked={enabled}
										onCheckedChange={(checked: boolean) => { handleFieldChange(setEnabled)(checked); }}
									/>
									<Label htmlFor="module-enabled">Enabled</Label>
								</div>
							</>
						) : (
							<>
								<div className="grid gap-2">
									<Label htmlFor="module-name">Name</Label>
									<Input
										id="module-name"
										placeholder="Dashboard"
										value={name}
										onChange={(e) => { handleFieldChange(setName)(e.target.value); }}
									/>
								</div>

								<div className="grid gap-2">
									<Label htmlFor="module-slug">Slug</Label>
									<Input
										id="module-slug"
										placeholder="dashboard"
										value={slug}
										onChange={(e) => { handleFieldChange(setSlug)(e.target.value); }}
									/>
									<p className="text-xs text-muted-foreground">
										URL path for this module (lowercase letters, numbers, and hyphens only)
									</p>
								</div>

								<div className="grid gap-2">
									<Label>Icon</Label>
									<Popover open={iconPickerOpen} onOpenChange={setIconPickerOpen}>
										<PopoverTrigger asChild>
											<Button variant="outline" className="w-full justify-between">
												<span className="flex items-center gap-2">
													<PreviewIcon className="h-4 w-4" />
													{icon || "LayoutDashboard"}
												</span>
												<ChevronDown className="h-4 w-4 opacity-50" />
											</Button>
										</PopoverTrigger>
										<PopoverContent className="w-80 p-3" align="start">
											<div className="grid grid-cols-6 gap-2">
												{ICON_OPTIONS.map((iconName) => {
													const IconComponent = iconMap[iconName] ?? LayoutDashboard;
													const isSelected = (icon || "LayoutDashboard") === iconName;
													return (
														<button
															key={iconName}
															type="button"
															onClick={() => {
																handleFieldChange(setIcon)(iconName);
																setIconPickerOpen(false);
															}}
															className={cn(
																"flex h-9 w-9 items-center justify-center rounded-md border transition-colors hover:bg-accent",
																isSelected && "border-primary bg-accent"
															)}
															title={iconName}
														>
															<IconComponent className="h-4 w-4" />
														</button>
													);
												})}
											</div>
										</PopoverContent>
									</Popover>
								</div>

								<div className="grid gap-2">
									<Label htmlFor="module-group">Group</Label>
									<Select
										value={groupId ?? "ungrouped"}
										onValueChange={(v) => { handleFieldChange(setGroupId)(v === "ungrouped" ? null : v); }}
									>
										<SelectTrigger>
											<SelectValue placeholder="Select a group" />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="ungrouped">Ungrouped</SelectItem>
											{groups.map((group) => (
												<SelectItem key={group.id} value={group.id}>
													{group.name ?? "(No name)"}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>

								<div className="flex items-center gap-2 pt-2">
									<Checkbox
										id="module-enabled"
										checked={enabled}
										onCheckedChange={(checked: boolean) => { handleFieldChange(setEnabled)(checked); }}
									/>
									<Label htmlFor="module-enabled">Enabled</Label>
								</div>
							</>
						)}
					</CardContent>
				</Card>

				{/* Workflow Settings Card — hidden when creating a custom module */}
				{!(isCreateMode && creationMode === "custom") && (
					<Card>
						<CardHeader>
							<CardTitle>Workflow Settings</CardTitle>
							<CardDescription>
								Configure how the module&apos;s workflow behaves.
							</CardDescription>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="grid gap-2">
								<Label htmlFor="module-description">Description</Label>
								<Input
									id="module-description"
									placeholder="Brief description of this module"
									value={description}
									onChange={(e) => { handleFieldChange(setDescription)(e.target.value); }}
								/>
								<p className="text-xs text-muted-foreground">
									Shown to users when viewing this module
								</p>
							</div>

							<div className="grid gap-2">
								<Label htmlFor="module-hero">Hero Image URL</Label>
								<Input
									id="module-hero"
									placeholder="https://example.com/image.jpg"
									value={heroImage}
									onChange={(e) => { handleFieldChange(setHeroImage)(e.target.value); }}
								/>
								<p className="text-xs text-muted-foreground">
									Optional banner image for the module header
								</p>
							</div>

							<div className="flex items-center gap-2 pt-2">
								<Checkbox
									id="module-sequential"
									checked={sequentialExecution}
									onCheckedChange={(checked: boolean) => { handleFieldChange(setSequentialExecution)(checked); }}
								/>
								<Label htmlFor="module-sequential">Sequential Execution</Label>
							</div>
							<p className="text-xs text-muted-foreground pl-6">
								When enabled, users must complete actions in order
							</p>
						</CardContent>
					</Card>
				)}
			</div>

			{/* Workflow Actions Section */}
			{!isCreateMode && moduleId && (
				<Card className="mt-6">
					<CardContent className="pt-6">
						<ActionEditor moduleId={moduleId} />
					</CardContent>
				</Card>
			)}
			{isCreateMode && creationMode === "workflow" && (
				<Card className="mt-6">
					<CardContent className="py-12 text-center">
						<p className="text-muted-foreground">
							Save the module first to configure workflow actions.
						</p>
					</CardContent>
				</Card>
			)}
		</EditorShell>
	);
}
